import React from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../../routes/paths";
import { TYPE_LABEL, money, dayLabel, entryDateLabel } from "../ledgerGrouping";

// Compact rows (2026-09-12, "show more records in a single screen"): normal
// reading size (12.5px was too small to read - user), tighter padding than the
// default table, one line per entry - a long description is cut with "..." and
// shown in full on hover.
const cell = { padding: "5px 10px", fontSize: 14, lineHeight: 1.4, verticalAlign: "middle", whiteSpace: "nowrap" };
const numCell = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };
// maxWidth 0 + width 100% lets the description take whatever width is left
// and truncate inside it instead of widening the table.
const descCell = { ...cell, maxWidth: 0, width: "100%", overflow: "hidden", textOverflow: "ellipsis" };
const headCell = { ...cell, fontWeight: 600, background: "#F8F9FA" };
// The Overview scrolls the ledger inside a fixed-height box; the column
// headings stay visible at the top of it.
const stickyHead = { position: "sticky", top: 0, zIndex: 1 };
const HIT_BG = "#FFF3CD";

// Date, Description, Debit, Credit, Balance, Type, Reference (order set
// 2026-09-12 by instruction) - the two Running columns were removed 2026-09-09.
// Receipt No added 2026-09-22 (Batch193): the number entered when the voucher was paid.
const COLUMN_COUNT = 8;

/**
 * The Fund -> Day ledger flattened into display lines: fund heading (only
 * when there is more than one fund), day heading, Opening, entries, Closing.
 * Flat so the Overview's full-screen view can cut it into screen-sized
 * pages; the Ledger page draws the same lines, one table per fund.
 */
export const ledgerLines = (fundGroups, withFundRows = fundGroups.length > 1) => {
  const lines = [];
  fundGroups.forEach((fund) => {
    if (withFundRows) lines.push({ kind: "fund", key: `f-${fund.fundId}`, fund });
    fund.days.forEach((day) => {
      const key = `${fund.fundId}-${day.key}`;
      const fundName = withFundRows ? fund.fundName : null;
      lines.push({ kind: "day", key: `d-${key}`, day, fundName });
      lines.push({ kind: "opening", key: `o-${key}`, day });
      day.rows.forEach((r) => lines.push({ kind: "entry", key: `e-${r.ledgerId}`, r }));
      lines.push({ kind: "closing", key: `c-${key}`, day });
    });
  });
  return lines;
};

const isHeading = (line) => line.kind === "fund" || line.kind === "day";

/**
 * Cuts ledger lines into pages of `size` lines. A heading is never left alone
 * at the bottom of a page, and a page that starts part-way through a day
 * repeats that day's heading marked "(continued)", so every page reads on
 * its own.
 */
export const splitLedgerPages = (lines, size) => {
  const pages = [];
  let current = [];
  let lastDay = null;
  lines.forEach((line) => {
    if (current.length >= size) {
      const carried = [];
      while (current.length && isHeading(current[current.length - 1])) carried.unshift(current.pop());
      if (current.length) pages.push(current);
      current = carried;
      if (!carried.length && !isHeading(line) && lastDay) {
        current.push({ ...lastDay, key: `${lastDay.key}-cont-${pages.length}`, continued: true });
      }
    }
    if (line.kind === "day") lastDay = line;
    current.push(line);
  });
  if (current.length) pages.push(current);
  return pages;
};

const isHighlighted = (r, highlightRefs) =>
  r.referenceType === "Voucher" && r.referenceNumber != null && highlightRefs.has(r.referenceNumber);

const LedgerLine = ({ line, highlightRefs }) => {
  if (line.kind === "fund") {
    const { fund } = line;
    return (
      <tr>
        <td colSpan={COLUMN_COUNT} style={{ ...cell, fontWeight: 700, background: "#EEF1F4" }}>
          {fund.fundName} <span className="fw-normal text-muted">({fund.fundCode} &middot; {fund.branchCode})</span>
        </td>
      </tr>
    );
  }
  if (line.kind === "day") {
    return (
      <tr className="table-light">
        <td colSpan={COLUMN_COUNT} style={{ ...cell, fontWeight: 700 }}>
          {dayLabel(line.day.date)}
          {line.continued && <span className="fw-normal text-muted"> (continued)</span>}
          {line.fundName && <span className="fw-normal text-muted"> &middot; {line.fundName}</span>}
        </td>
      </tr>
    );
  }
  if (line.kind === "opening") {
    return (
      <tr className="fst-italic text-muted">
        <td style={cell} />
        <td style={descCell}>Opening Balance</td>
        <td style={numCell} />
        <td style={numCell} />
        <td style={numCell}>{money(line.day.openingBalance)}</td>
        <td style={cell} />
        <td style={cell} />
        <td style={cell} />
      </tr>
    );
  }
  if (line.kind === "closing") {
    const { day } = line;
    // The day's OWN debit/credit totals (reset every day) - not the removed
    // never-resetting Running columns.
    return (
      <tr className="fw-bold">
        <td style={cell} />
        <td style={descCell}>
          Closing Balance <span className="fw-normal text-muted">(day total)</span>
        </td>
        <td style={numCell}>{day.totalDebit ? money(day.totalDebit) : "-"}</td>
        <td style={numCell}>{day.totalCredit ? money(day.totalCredit) : "-"}</td>
        <td style={numCell}>{money(day.closingBalance)}</td>
        <td style={cell} />
        <td style={cell} />
        <td style={cell} />
      </tr>
    );
  }
  const { r } = line;
  const hit = highlightRefs && isHighlighted(r, highlightRefs);
  const rowStyle = !highlightRefs ? undefined : hit ? undefined : { opacity: 0.4 };
  const hitCell = (base) => (hit ? { ...base, background: HIT_BG, fontWeight: 600 } : base);
  return (
    <tr style={rowStyle}>
      <td style={hitCell(cell)}>{entryDateLabel(r.transactionDate)}</td>
      <td style={hitCell(descCell)} title={r.description || undefined}>
        {r.description || "-"}
      </td>
      <td style={hitCell(numCell)}>{Number(r.debitAmount) ? money(r.debitAmount) : "-"}</td>
      <td style={hitCell(numCell)}>{Number(r.creditAmount) ? money(r.creditAmount) : "-"}</td>
      <td style={{ ...hitCell(numCell), fontWeight: 600 }}>{money(r.balanceAfterTransaction)}</td>
      <td style={hitCell(cell)}>{TYPE_LABEL[r.transactionType] || r.transactionType}</td>
      {/* Drill-down: a voucher-referenced row opens that voucher. The guid
          comes from the ledger row itself (ReferenceRecordGuid); rows without
          one - refills, adjustments, opening balances - stay plain text. */}
      <td style={hitCell(cell)}>
        {r.referenceRecordGuid ? (
          <Link to={paths.pettyCashVoucherDetail(r.referenceRecordGuid)} title="Open this voucher">
            {r.referenceNumber || "View voucher"}
          </Link>
        ) : (
          r.referenceNumber || "-"
        )}
      </td>
      <td style={hitCell(cell)} title={r.receiptNumber ? "Receipt number entered when the voucher was paid" : undefined}>
        {r.receiptNumber || "-"}
      </td>
    </tr>
  );
};

/**
 * One ledger table over a list of lines.
 *
 * `highlightRefs` (Overview only): a Set of voucher numbers. The ledger is
 * never filtered by voucher criteria - hiding entries would break the running
 * balance each day's opening carries over from - so matching payments and
 * returns are highlighted and everything else is dimmed instead.
 */
export const LedgerLinesTable = ({ lines, highlightRefs = null, sticky = false }) => {
  const th = (extra) => ({ ...headCell, ...(sticky ? stickyHead : null), ...extra });
  return (
    <table className="table table-bordered table-sm mb-0">
      <thead>
        <tr>
          <th style={th({ width: 135 })}>Date</th>
          <th style={th()}>Description</th>
          <th style={th({ width: 115, textAlign: "right" })}>Debit</th>
          <th style={th({ width: 115, textAlign: "right" })}>Credit</th>
          <th style={th({ width: 125, textAlign: "right" })}>Balance</th>
          <th style={th({ width: 145 })}>Type</th>
          <th style={th({ width: 170 })}>Reference</th>
          <th style={th({ width: 140 })}>Receipt No</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <LedgerLine key={line.key} line={line} highlightRefs={highlightRefs} />
        ))}
      </tbody>
    </table>
  );
};

/**
 * The Fund -> Day ledger, one table per fund - shared by the Ledger page and
 * the Petty Cash Overview (moved out of LedgerPage 2026-09-15, same layout).
 */
//
// `flat` (2026-09-22): the fund's `flatRows` (sorted by voucher or receipt
// number, see ledgerGrouping.applyLedgerSort) as plain entry lines - no day
// headings, no opening / closing balances, which only mean something in date
// order. Each row still shows the balance the fund stood at after it.
const LedgerTable = ({ fundGroups, highlightRefs = null, sticky = false, flat = false }) => {
  const showFundHeader = fundGroups.length > 1;
  return fundGroups.map((fund) => (
    <div key={fund.fundId} className="mb-4">
      {showFundHeader && (
        <h6 className="mb-2">
          {fund.fundName} <span className="text-muted">({fund.fundCode} &middot; {fund.branchCode})</span>
        </h6>
      )}
      <div className={sticky ? undefined : "table-responsive"}>
        <LedgerLinesTable
          lines={flat ? (fund.flatRows || []).map((r) => ({ kind: "entry", key: `e-${r.ledgerId}`, r })) : ledgerLines([fund], false)}
          highlightRefs={highlightRefs}
          sticky={sticky}
        />
      </div>
    </div>
  ));
};

export default LedgerTable;
